import React, { useRef, useState } from "react";
import moment from "moment";
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import matchSorter from 'match-sorter';
import ReactExport from "react-data-export";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

const TablaReporteMovimientos = ({ reporteData }) => {
    const tableRef = useRef();
    const [analisisExpandido, setAnalisisExpandido] = useState(false);
    const [recomendacionesExpandido, setRecomendacionesExpandido] = useState(false);
    
    if (!reporteData) {
        return (
            <div className="alert alert-warning mt-3 shadow-sm border-0">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                No hay datos disponibles para el reporte
            </div>
        );
    }

    const { infoTurno, resumenPagos, totalesGenerales, detalleVentas } = reporteData;

    const primaryColor = 'rgb(53, 96, 90)';
    const secondaryColor = 'rgb(1, 165, 156)';
    const lightColor = 'rgba(53, 96, 90, 0.08)';

    const detalleVentasFormatted = detalleVentas.map((venta, index) => ({
        ...venta,
        id: index,
        FechaFormateada: moment(venta.FechaEmision).format('DD/MM/YYYY hh:mm'),
        TotalComprobante: parseFloat(venta.Total || 0).toFixed(2),
    }));

    const getFilteredData = () => {
        if (!tableRef.current) {
            return detalleVentasFormatted;
        }
        
        const tableInstance = tableRef.current;
        const resolvedState = tableInstance.getResolvedState();
        
        if (resolvedState.filtered && resolvedState.filtered.length > 0) {
            return tableInstance.getData();
        }
        
        return detalleVentasFormatted;
    };

    const prepareExportData = () => {
        const filteredData = getFilteredData();
        
        return filteredData.map((row, index) => {
            const tiposPago = row.TiposPago ? row.TiposPago.split(' | ') : [];
            const montosPago = row.MontosPago ? row.MontosPago.split(' | ') : [];
            
            let mediosPagoTexto = '';
            let montosTexto = '';
            
            if (tiposPago.length === montosPago.length) {
                mediosPagoTexto = tiposPago.join(' | ');
                montosTexto = montosPago.map(monto => `S/ ${parseFloat(monto).toFixed(2)}`).join(' | ');
            } else {
                mediosPagoTexto = row.TiposPago || '';
                montosTexto = row.MontosPago ? `S/ ${parseFloat(row.MontosPago).toFixed(2)}` : '';
            }
            
            return {
                "N°": index + 1,
                "RUC": row.ruc || "",
                "CLIENTE": row.Cliente || "",
                "FECHA MOVIMIENTO": row.FechaFormateada || "",
                "SERIE": row.Serie || "",
                "NUMERO COMPROBANTE": row.NumeroComprobante || "",
                "MEDIO DE PAGO": mediosPagoTexto,
                "MONTO": montosTexto,
                "TOTAL COMPROBANTE": row.TotalComprobante || "0.00"
            };
        });
    };

    const dataExport = [
        {
            columns: [
                { title: "N°", width: { wpx: 50 } },
                { title: "RUC", width: { wpx: 100 } },
                { title: "CLIENTE", width: { wpx: 200 } },
                { title: "FECHA MOVIMIENTO", width: { wpx: 120 } },
                { title: "SERIE", width: { wpx: 60 } },
                { title: "NÚMERO COMPROBANTE", width: { wpx: 120 } },
                { title: "MEDIO DE PAGO", width: { wpx: 150 } },
                { title: "MONTO(S)", width: { wpx: 150 } },
                { title: "TOTAL COMPROBANTE", width: { wpx: 120 } }
            ],
            data: prepareExportData().map(item => [
                item["N°"],
                item["RUC"],
                item["CLIENTE"],
                item["FECHA MOVIMIENTO"],
                item["SERIE"],
                item["NUMERO COMPROBANTE"],
                item["MEDIO DE PAGO"],
                item["MONTO"],
                `S/ ${item["TOTAL COMPROBANTE"]}`
            ])
        }
    ];

    const columnsDetalleVentas = [
        {
            Header: "RUC",
            accessor: "ruc",
            width: 130,
            style: { textAlign: 'center' },
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["ruc"] }),
            filterAll: true
        },
        {
            Header: "Cliente",
            accessor: "Cliente",
            width: 250,
            style: { textAlign: 'center' },
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["Cliente"] }),
            filterAll: true
        },
        {
            Header: "Fecha Movimiento",
            accessor: "FechaFormateada",
            width: 150,
            style: { textAlign: 'center' },
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["FechaFormateada"] }),
            filterAll: true
        },
        {
            Header: "Serie",
            accessor: "Serie",
            width: 80,
            style: { textAlign: 'center' },
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["Serie"] }),
            filterAll: true
        },
        {
            Header: "Numero Comprobante",
            accessor: "NumeroComprobante",
            width: 100,
            style: { textAlign: 'center' },
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["NumeroComprobante"] }),
            filterAll: true
        },
        {
            Header: "Medio Pago",
            id: "TiposPago",
            width: 200,
            style: { textAlign: 'center' },
            accessor: d => d.TiposPago || '',
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { 
                keys: ["TiposPago"],
                threshold: matchSorter.rankings.CONTAINS
            }),
            filterAll: true,
            Cell: ({ value }) => {
                if (!value) return '';
                
                const tipos = value.split(' | ');
                return tipos.map((tipo, idx) => (
                    <div key={idx} >
                        {tipo.trim()}
                    </div>
                ));
            }
        },
        {
            Header: "Montos",
            id: "MontosPago",
            width: 140,
            style: { textAlign: 'center' },
            accessor: d => d.MontosPago || '',
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { 
                keys: ["MontosPago"],
                threshold: matchSorter.rankings.CONTAINS
            }),
            filterAll: true,
            Cell: ({ value }) => {
                if (!value) return '';
                
                const montos = value.split(' | ');
                return montos.map((monto, idx) => {
                    const montoNum = parseFloat(monto);
                    return (
                        <div key={idx} >
                            S/ {!isNaN(montoNum) ? montoNum.toFixed(2) : '0.00'}
                        </div>
                    );
                });
            }
        },
        {
            Header: "Total",
            accessor: "TotalComprobante",
            width: 120,
            style: { textAlign: 'center' },
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["TotalComprobante"] }),
            filterAll: true,
            Cell: ({ value }) => (
                <div >{value}</div>
            )
        }
    ];

    const calcularAnalisisDetallado = () => {
        const totalVentas = parseFloat(totalesGenerales?.MontoTotalVentas || 0);
        const totalComprobantes = totalesGenerales?.TotalVentas || 0;
        const montoInicial = parseFloat(totalesGenerales?.MontoInicial || 0);
        const montoFinal = parseFloat(totalesGenerales?.MontoFinalEfectivo || 0);
        
        const mejorMedioPago = resumenPagos.length > 0 ? 
            resumenPagos.reduce((prev, current) => 
                parseFloat(prev.TotalMonto) > parseFloat(current.TotalMonto) ? prev : current
            ) : null;
        
        const peorMedioPago = resumenPagos.length > 0 ? 
            resumenPagos.reduce((prev, current) => 
                parseFloat(prev.TotalMonto) < parseFloat(current.TotalMonto) ? prev : current
            ) : null;
        
        const clientesMap = {};
        detalleVentas.forEach(venta => {
            const cliente = venta.Cliente || 'Sin nombre';
            if (!clientesMap[cliente]) {
                clientesMap[cliente] = { 
                    cantidad: 0, 
                    total: 0,
                    ComprobantePromedio: 0 
                };
            }
            clientesMap[cliente].cantidad++;
            clientesMap[cliente].total += parseFloat(venta.Total || 0);
        });
        
        Object.keys(clientesMap).forEach(cliente => {
            clientesMap[cliente].ComprobantePromedio = clientesMap[cliente].total / clientesMap[cliente].cantidad;
        });
        
        const topClientes = Object.entries(clientesMap)
            .sort((a, b) => b[1].total - a[1].total)
            .slice(0, 5)
            .map(([nombre, datos]) => ({ nombre, ...datos }));
        
        const mejorCliente = topClientes.length > 0 ? topClientes[0] : null;
        const clienteFrecuente = Object.entries(clientesMap)
            .sort((a, b) => b[1].cantidad - a[1].cantidad)[0];
        
        const horasMap = {};
        detalleVentas.forEach(venta => {
            const hora = moment(venta.FechaEmision).format('HH:00');
            if (!horasMap[hora]) {
                horasMap[hora] = { cantidad: 0, total: 0 };
            }
            horasMap[hora].cantidad++;
            horasMap[hora].total += parseFloat(venta.Total || 0);
        });
        
        const mejorHora = Object.entries(horasMap)
            .sort((a, b) => b[1].total - a[1].total)[0];
        
        const horaMasFrecuente = Object.entries(horasMap)
            .sort((a, b) => b[1].cantidad - a[1].cantidad)[0];
        
        const seriesMap = {};
        detalleVentas.forEach(venta => {
            const serie = venta.Serie || 'Sin serie';
            if (!seriesMap[serie]) {
                seriesMap[serie] = { cantidad: 0, total: 0 };
            }
            seriesMap[serie].cantidad++;
            seriesMap[serie].total += parseFloat(venta.Total || 0);
        });
        
        const mejorSerie = Object.entries(seriesMap)
            .sort((a, b) => b[1].total - a[1].total)[0];
        
        const ComprobantePromedio = totalComprobantes > 0 ? totalVentas / totalComprobantes : 0;
        let duracionHoras = 8;
        if (infoTurno[0]?.FechaInicio && infoTurno[0]?.FechaFin) {
            duracionHoras = moment(infoTurno[0].FechaFin).diff(moment(infoTurno[0].FechaInicio), 'hours', true);
            duracionHoras = duracionHoras > 0 ? duracionHoras : 8;
        }
        
        const ventasPorHora = totalVentas / duracionHoras;
        const comprobantesPorHora = totalComprobantes / duracionHoras;
        const eficienciaCaja = montoInicial > 0 ? ((montoFinal - montoInicial) / montoInicial) * 100 : 0;
        const porcentajeMedioPrincipal = mejorMedioPago ? 
            (parseFloat(mejorMedioPago.TotalMonto) / totalVentas) * 100 : 0;
        
        return {
            ComprobantePromedio,
            duracionHoras,
            ventasPorHora,
            comprobantesPorHora,
            eficienciaCaja,
            porcentajeMedioPrincipal,
            
            mejorMedioPago: mejorMedioPago ? {
                nombre: mejorMedioPago.MedioPago,
                monto: parseFloat(mejorMedioPago.TotalMonto),
                porcentaje: porcentajeMedioPrincipal,
                ventas: mejorMedioPago.CantidadVentas
            } : null,
            
            peorMedioPago: peorMedioPago ? {
                nombre: peorMedioPago.MedioPago,
                monto: parseFloat(peorMedioPago.TotalMonto),
                ventas: peorMedioPago.CantidadVentas
            } : null,
            
            mejorCliente: mejorCliente ? {
                nombre: mejorCliente.nombre,
                monto: mejorCliente.total,
                compras: mejorCliente.cantidad,
                ComprobantePromedio: mejorCliente.ComprobantePromedio
            } : null,
            
            clienteFrecuente: clienteFrecuente ? {
                nombre: clienteFrecuente[0],
                visitas: clienteFrecuente[1].cantidad,
                montoTotal: clienteFrecuente[1].total
            } : null,
            
            mejorHora: mejorHora ? {
                hora: mejorHora[0],
                monto: mejorHora[1].total,
                ventas: mejorHora[1].cantidad
            } : null,
            
            horaMasFrecuente: horaMasFrecuente ? {
                hora: horaMasFrecuente[0],
                ventas: horaMasFrecuente[1].cantidad,
                monto: horaMasFrecuente[1].total
            } : null,
            
            mejorSerie: mejorSerie ? {
                serie: mejorSerie[0],
                monto: mejorSerie[1].total,
                comprobantes: mejorSerie[1].cantidad
            } : null,
            
            topClientes,
            totalVentas,
            totalComprobantes
        };
    };

    const analisis = calcularAnalisisDetallado();

    const generarRecomendaciones = () => {
        const recomendaciones = [];
        
        if (analisis.mejorMedioPago && analisis.mejorMedioPago.porcentaje > 60) {
            recomendaciones.push({
                tipo: 'Medio de Pago',
                titulo: 'Diversificar medios de pago',
                descripcion: `El ${analisis.mejorMedioPago.nombre} representa el ${analisis.mejorMedioPago.porcentaje.toFixed(1)}% de las ventas. Considere promover otros medios.`,
                prioridad: 'alta',
                icono: 'fa-credit-card'
            });
        }
        
        if (analisis.peorMedioPago && analisis.peorMedioPago.ventas < 3) {
            recomendaciones.push({
                tipo: 'Medio de Pago',
                titulo: 'Revisar medio poco utilizado',
                descripcion: `El ${analisis.peorMedioPago.nombre} solo se usó ${analisis.peorMedioPago.ventas} vez/veces. Evalúe si es necesario mantenerlo.`,
                prioridad: 'media',
                icono: 'fa-chart-line'
            });
        }
        
        if (analisis.mejorCliente && analisis.mejorCliente.monto > (analisis.totalVentas * 0.3)) {
            recomendaciones.push({
                tipo: 'Clientes',
                titulo: 'Programa de fidelización',
                descripcion: `${analisis.mejorCliente.nombre} representa el ${((analisis.mejorCliente.monto / analisis.totalVentas) * 100).toFixed(1)}% de las ventas. Cree un programa especial.`,
                prioridad: 'alta',
                icono: 'fa-crown'
            });
        }
        
        if (analisis.clienteFrecuente && analisis.clienteFrecuente.visitas >= 5) {
            recomendaciones.push({
                tipo: 'Clientes',
                titulo: 'Reconocimiento cliente frecuente',
                descripcion: `${analisis.clienteFrecuente.nombre} ha visitado ${analisis.clienteFrecuente.visitas} veces. Ofrezca beneficios por fidelidad.`,
                prioridad: 'media',
                icono: 'fa-star'
            });
        }
        
        if (analisis.mejorHora && analisis.mejorHora.monto > (analisis.ventasPorHora * 2)) {
            recomendaciones.push({
                tipo: 'Horarios',
                titulo: 'Optimizar horario pico',
                descripcion: `La hora ${analisis.mejorHora.hora} genera S/ ${analisis.mejorHora.monto.toFixed(2)} (${analisis.mejorHora.ventas} ventas). Asegure personal suficiente.`,
                prioridad: 'alta',
                icono: 'fa-clock'
            });
        }
        
        if (analisis.ComprobantePromedio < 30) {
            recomendaciones.push({
                tipo: 'Ventas',
                titulo: 'Aumentar Comprobante promedio',
                descripcion: `El Comprobante promedio es S/ ${analisis.ComprobantePromedio.toFixed(2)}. Implemente estrategias de upselling y cross-selling.`,
                prioridad: 'alta',
                icono: 'fa-chart-bar'
            });
        }
        
        if (analisis.comprobantesPorHora < 4) {
            recomendaciones.push({
                tipo: 'Eficiencia',
                titulo: 'Mejorar velocidad de atención',
                descripcion: `Se procesan ${analisis.comprobantesPorHora.toFixed(1)} comprobantes/hora. Capacite en agilidad de procesos.`,
                prioridad: 'media',
                icono: 'fa-bolt'
            });
        }
        
        if (analisis.eficienciaCaja < 15 && analisis.eficienciaCaja > 0) {
            recomendaciones.push({
                tipo: 'Caja',
                titulo: 'Optimizar gestión de efectivo',
                descripcion: `Rendimiento de caja: ${analisis.eficienciaCaja.toFixed(1)}%. Ajuste monto inicial para mejorar rentabilidad.`,
                prioridad: 'media',
                icono: 'fa-cash-register'
            });
        }
        
        return recomendaciones;
    };

    const recomendaciones = generarRecomendaciones();

    return (
        <div className="mt-4 animated fadeIn">
            {/* 1. TARJETAS DE RESUMEN */}
            <div className="row mb-4">
                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white" style={{backgroundColor: primaryColor}}>
                            <h6 className="mb-0">
                                <i className="fas fa-cash-register mr-2"></i>
                                Monto Inicial
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="h4 mb-0 font-weight-bold" style={{ color: primaryColor }}>
                                S/ {parseFloat(totalesGenerales?.MontoInicial).toFixed(2)}
                            </div>
                            <small className="text-muted">Monto de apertura</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white" style={{backgroundColor: primaryColor}}>
                            <h6 className="mb-0">
                                <i className="fas fa-chart-line mr-2"></i>
                                Total Ventas
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="h4 mb-0 font-weight-bold" style={{ color: primaryColor }}>
                                S/ {parseFloat(totalesGenerales?.MontoTotalVentas).toFixed(2)}
                            </div>
                            <small className="text-muted">Suma total de ventas</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white" style={{backgroundColor: primaryColor}}>
                            <h6 className="mb-0">
                                <i className="fas fa-money-bill-wave mr-2"></i>
                                Efectivo en Caja
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="h4 mb-0 font-weight-bold" style={{ color: primaryColor }}>
                                S/ {parseFloat(totalesGenerales?.MontoFinalEfectivo).toFixed(2)}
                            </div>
                            <small className="text-muted">Efectivo total disponible</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white" style={{backgroundColor: primaryColor}}>
                            <h6 className="mb-0">
                                <i className="fas fa-file-invoice mr-2"></i>
                                Comprobantes
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="h4 mb-0 font-weight-bold" style={{ color: primaryColor }}>
                                {totalesGenerales?.TotalVentas}
                            </div>
                            <small className="text-muted">Total de operaciones</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. SECCION: INFO Y DISTRIBUCION EN CARDS */}
            <div className="row mb-4">
                <div className="col-lg-6 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white" style={{backgroundColor: primaryColor}}>
                            <h6 className="mb-0">
                                <i className="fas fa-user-circle mr-2"></i>
                                Información del Responsable
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-6 mb-3">
                                    <small className="d-block text-muted mb-1">Usuario:</small>
                                    <div className="p-2 rounded" style={{ backgroundColor: lightColor }}>
                                        <strong style={{ color: primaryColor }}>
                                            <i className="fas fa-user mr-2" style={{ color: secondaryColor }}></i>
                                            {infoTurno[0]?.NombreUsuario}
                                        </strong>
                                    </div>
                                </div>
                                <div className="col-6 mb-3">
                                    <small className="d-block text-muted mb-1">Caja:</small>
                                    <div className="p-2 rounded" style={{ backgroundColor: lightColor }}>
                                        <strong style={{ color: primaryColor }}>
                                            <i className="fas fa-cash-register mr-2" style={{ color: secondaryColor }}></i>
                                            {infoTurno[0]?.NombreCaja}
                                        </strong>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <small className="d-block text-muted mb-2">Periodo:</small>
                                    <div className="p-3 rounded" style={{ 
                                        backgroundColor: lightColor,
                                        border: `1px dashed ${secondaryColor}40`
                                    }}>
                                        <div className="d-flex align-items-center">
                                            <i className="far fa-calendar-alt mr-3" style={{ 
                                                color: secondaryColor,
                                                fontSize: '1.5rem'
                                            }}></i>
                                            <div>
                                                <div className="small font-weight-bold" style={{ color: primaryColor }}>
                                                    Inicio: {moment(infoTurno[0]?.FechaInicio).format('DD/MM/YYYY HH:mm')}
                                                </div>
                                                <div className="small font-weight-bold" style={{ color: primaryColor }}>
                                                    {infoTurno[0]?.FechaFin ? 
                                                        `Fin: ${moment(infoTurno[0]?.FechaFin).format('DD/MM/YYYY HH:mm')}` : 
                                                        <span style={{ color: secondaryColor }}>EN CURSO</span>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card de Resumen de Pagos */}
                <div className="col-lg-6 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white" style={{backgroundColor: primaryColor}}>
                            <h6 className="mb-0">
                                <i className="fas fa-chart-pie mr-2"></i>
                                Distribución de Pagos
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="">
                                {resumenPagos.map((pago, index) => (
                                    <div key={index} className="mb-2">
                                        <div className="d-flex justify-content-between align-items-center mb-1" style={{ backgroundColor: lightColor }}>
                                            <div className="d-flex align-items-center">
                                                <span className="font-weight-bold " style={{ color: primaryColor, width:'100px' }}>
                                                    {pago.MedioPago}
                                                </span>
                                            </div>
                                            <span className="font-weight-bold" style={{ color: secondaryColor, textAlign: 'right' }}>
                                                S/ {parseFloat(pago.TotalMonto).toFixed(2)}
                                            </span>
                                            <span style={{textAlign: 'right' }}>{pago.CantidadVentas} comprobantes</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 mb-4 seccion-btn-pdf-excel" style={{ textAlign: 'right' }}>
                <ExcelFile
                    element={
                        <button
                            className="btn-excel-mfp ml-2"
                            title="Descarga reporte de Ventas en EXCEL"
                        >
                            <i className="fas fa-file-excel mr-2"></i>
                            Excel
                        </button>
                    }
                    filename={`ReporteVentas_${moment().format('YYYY-MM-DD_HHmmss')}`}
                >
                    <ExcelSheet
                        dataSet={dataExport}
                        name="Ventas"
                    />
                </ExcelFile>
            </div>
            
            <div className="row">
                <div className="col-12">
                    <div className="shadow-sm mb-4 border-0">
                        <div className="card-body p-0">
                            <ReactTable
                                ref={tableRef}
                                data={detalleVentasFormatted}
                                columns={columnsDetalleVentas}
                                filterable={true}
                                defaultPageSize={10}
                                className="-striped -highlight"
                                previousText='Anterior'
                                nextText='Siguiente'
                                pageText='Página'
                                ofText='de'
                                rowsText='filas'
                                noDataText='No se encontraron comprobantes'
                                defaultFilterMethod={(filter, row) => {
                                    const id = filter.pivotId || filter.id;
                                    if (row[id] !== null) {
                                        return String(row[id])
                                            .toLowerCase()
                                            .includes(filter.value.toLowerCase());
                                    }
                                    return true;
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* SECCIÓN: ANÁLISIS INTELIGENTE (BETA) - DESPLEGABLE */}
            <div className="row mt-4">
                <div className="col-md-12">
                    <div className="card border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${primaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white d-flex justify-content-between align-items-center" style={{backgroundColor: primaryColor}}>
                            <div className="d-flex align-items-center">
                                <h6 className="mb-0">
                                    <i className="fas fa-brain mr-2"></i>
                                    Análisis Inteligente
                                </h6>
                                <span className="badge badge-warning ml-2">BETA</span>
                            </div>
                            <div>
                                <button 
                                    className="btn btn-sm btn-light"
                                    onClick={() => setAnalisisExpandido(!analisisExpandido)}
                                >
                                    <i className={`fas fa-chevron-${analisisExpandido ? 'up' : 'down'} mr-1`}></i>
                                    {analisisExpandido ? 'Ocultar' : 'Ver análisis'}
                                </button>
                            </div>
                        </div>
                        
                        {analisisExpandido && (
                            <div className="card-body">
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle mr-2"></i>
                                    Esta sección está en desarrollo BETA. Proporciona insights basados en tus datos actuales.
                                </div>
                                
                                <div className="row">
                                    {/* MEJOR MEDIO DE PAGO */}
                                    <div className="col-md-4 mb-3">
                                        <div className="card h-100 border-0" style={{ backgroundColor: lightColor }}>
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="mr-3 p-2 rounded-circle" style={{ 
                                                        backgroundColor: secondaryColor,
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <i className="fas fa-trophy text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0" style={{ color: primaryColor }}>Mejor Medio de Pago</h6>
                                                        <small className="text-muted">Mayor generación de ingresos</small>
                                                    </div>
                                                </div>
                                                {analisis.mejorMedioPago ? (
                                                    <>
                                                        <h4 className="text-center mb-2" style={{ color: secondaryColor }}>
                                                            {analisis.mejorMedioPago.nombre}
                                                        </h4>
                                                        <div className="text-center">
                                                            <div className="h5 mb-1" style={{ color: primaryColor }}>
                                                                S/ {analisis.mejorMedioPago.monto.toFixed(2)}
                                                            </div>
                                                            <div className="small text-muted">
                                                                {analisis.mejorMedioPago.ventas} ventas • {analisis.mejorMedioPago.porcentaje.toFixed(1)}% del total
                                                            </div>
                                                        </div>
                                                        <div className="mt-3">
                                                            <div className="progress" style={{ height: '10px' }}>
                                                                <div 
                                                                    className="progress-bar bg-success" 
                                                                    style={{ width: `${Math.min(analisis.mejorMedioPago.porcentaje, 100)}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="text-center text-muted mb-0">Sin datos suficientes</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* MEJOR CLIENTE */}
                                    <div className="col-md-4 mb-3">
                                        <div className="card h-100 border-0" style={{ backgroundColor: lightColor }}>
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="mr-3 p-2 rounded-circle" style={{ 
                                                        backgroundColor: secondaryColor,
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <i className="fas fa-user-tie text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0" style={{ color: primaryColor }}>Cliente Destacado</h6>
                                                        <small className="text-muted">Mayor volumen de compras</small>
                                                    </div>
                                                </div>
                                                {analisis.mejorCliente ? (
                                                    <>
                                                        <h6 className="text-center mb-2 text-truncate" style={{ color: primaryColor }}>
                                                            {analisis.mejorCliente.nombre}
                                                        </h6>
                                                        <div className="text-center">
                                                            <div className="h5 mb-1" style={{ color: secondaryColor }}>
                                                                S/ {analisis.mejorCliente.monto.toFixed(2)}
                                                            </div>
                                                            <div className="small text-muted">
                                                                {analisis.mejorCliente.compras} compras • Comprobante: S/ {analisis.mejorCliente.ComprobantePromedio.toFixed(2)}
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 text-center">
                                                            <span className="badge" style={{ backgroundColor: primaryColor, color: 'white' }}>
                                                                Representa el {((analisis.mejorCliente.monto / analisis.totalVentas) * 100).toFixed(1)}% de ventas
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="text-center text-muted mb-0">Sin datos suficientes</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* MEJOR HORA */}
                                    <div className="col-md-4 mb-3">
                                        <div className="card h-100 border-0" style={{ backgroundColor: lightColor }}>
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="mr-3 p-2 rounded-circle" style={{ 
                                                        backgroundColor: secondaryColor,
                                                        width: '50px',
                                                        height: '50px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <i className="fas fa-clock text-white"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0" style={{ color: primaryColor }}>Hora Pico</h6>
                                                        <small className="text-muted">Mayor generación de ingresos</small>
                                                    </div>
                                                </div>
                                                {analisis.mejorHora ? (
                                                    <>
                                                        <h2 className="text-center mb-2" style={{ color: secondaryColor }}>
                                                            {analisis.mejorHora.hora}
                                                        </h2>
                                                        <div className="text-center">
                                                            <div className="h5 mb-1" style={{ color: primaryColor }}>
                                                                S/ {analisis.mejorHora.monto.toFixed(2)}
                                                            </div>
                                                            <div className="small text-muted">
                                                                {analisis.mejorHora.ventas} ventas en esta hora
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 text-center">
                                                            <span className="badge badge-info">
                                                                {((analisis.mejorHora.monto / analisis.ventasPorHora) * 100).toFixed(0)}% sobre el promedio
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="text-center text-muted mb-0">Sin datos suficientes</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* DETALLES ADICIONALES */}
                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header" style={{ backgroundColor: lightColor }}>
                                                <h6 className="mb-0" style={{ color: primaryColor }}>
                                                    <i className="fas fa-chart-bar mr-2"></i>
                                                    Top 5 Clientes
                                                </h6>
                                            </div>
                                            <div className="card-body p-0">
                                                <div className="list-group list-group-flush">
                                                    {analisis.topClientes.map((cliente, index) => (
                                                        <div key={index} className="list-group-item">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <div className="font-weight-bold" style={{ color: primaryColor }}>
                                                                        {index + 1}. {cliente.nombre}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {cliente.cantidad} compras • Comprobante: S/ {cliente.ComprobantePromedio.toFixed(2)}
                                                                    </small>
                                                                </div>
                                                                <div className="text-right">
                                                                    <div className="h5 mb-0" style={{ color: secondaryColor }}>
                                                                        S/ {cliente.total.toFixed(2)}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {((cliente.total / analisis.totalVentas) * 100).toFixed(1)}%
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header" style={{ backgroundColor: lightColor }}>
                                                <h6 className="mb-0" style={{ color: primaryColor }}>
                                                    <i className="fas fa-file-invoice mr-2"></i>
                                                    Análisis por Serie
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                {analisis.mejorSerie ? (
                                                    <div className="text-center p-3">
                                                        <h4 style={{ color: secondaryColor }}>Serie: {analisis.mejorSerie.serie}</h4>
                                                        <div className="h3 mt-2" style={{ color: primaryColor }}>
                                                            S/ {analisis.mejorSerie.monto.toFixed(2)}
                                                        </div>
                                                        <div className="text-muted">
                                                            {analisis.mejorSerie.comprobantes} comprobantes
                                                        </div>
                                                        <div className="mt-3">
                                                            <span className="badge badge-success">
                                                                Mejor rendimiento por serie
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-center text-muted">No hay datos de series disponibles</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SECCIÓN: RECOMENDACIONES ACCIONABLES (BETA) - DESPLEGABLE */}
            <div className="row mt-4">
                <div className="col-md-12">
                    <div className="card border-0 shadow-sm" style={{ 
                        borderTop: `4px solid ${secondaryColor}`,
                        backgroundColor: 'white'
                    }}>
                        <div className="card-header text-white d-flex justify-content-between align-items-center" style={{backgroundColor: primaryColor}}>
                            <div className="d-flex align-items-center">
                                <h6 className="mb-0">
                                    <i className="fas fa-lightbulb mr-2"></i>
                                    Recomendaciones Accionables
                                </h6>
                                <span className="badge badge-warning ml-2">BETA</span>
                            </div>
                            <div>
                                <button 
                                    className="btn btn-sm btn-light"
                                    onClick={() => setRecomendacionesExpandido(!recomendacionesExpandido)}
                                >
                                    <i className={`fas fa-chevron-${recomendacionesExpandido ? 'up' : 'down'} mr-1`}></i>
                                    {recomendacionesExpandido ? 'Ocultar' : 'Ver recomendaciones'}
                                </button>
                            </div>
                        </div>
                        
                        {recomendacionesExpandido && (
                            <div className="card-body">
                                <div className="alert alert-warning">
                                    <i className="fas fa-exclamation-triangle mr-2"></i>
                                    Estas recomendaciones son sugerencias basadas en el análisis de datos. Evalúe su aplicabilidad en su contexto específico.
                                </div>
                                
                                {recomendaciones.length > 0 ? (
                                    <div className="row">
                                        {recomendaciones.map((rec, index) => (
                                            <div key={index} className="col-md-6 mb-3">
                                                <div className={`card h-100 border-left-${rec.prioridad === 'alta' ? 'danger' : rec.prioridad === 'media' ? 'warning' : 'info'}`}>
                                                    <div className="card-body">
                                                        <div className="d-flex align-items-start">
                                                            <div className="mr-3">
                                                                <i className={`fas ${rec.icono} fa-2x`} style={{ 
                                                                    color: rec.prioridad === 'alta' ? '#dc3545' : 
                                                                           rec.prioridad === 'media' ? '#ffc107' : '#17a2b8'
                                                                }}></i>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <h6 className="card-title mb-1" style={{ color: primaryColor }}>
                                                                        {rec.titulo}
                                                                    </h6>
                                                                    <span className={`badge badge-${rec.prioridad === 'alta' ? 'danger' : rec.prioridad === 'media' ? 'warning' : 'info'}`}>
                                                                        {rec.prioridad === 'alta' ? 'Alta' : rec.prioridad === 'media' ? 'Media' : 'Baja'}
                                                                    </span>
                                                                </div>
                                                                <p className="card-text small text-muted mb-2">
                                                                    {rec.descripcion}
                                                                </p>
                                                                <div className="small">
                                                                    <span className="badge badge-light mr-2">
                                                                        <i className="fas fa-tag mr-1"></i>
                                                                        {rec.tipo}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="fas fa-check-circle fa-3x mb-3" style={{ color: secondaryColor }}></i>
                                        <h5 style={{ color: primaryColor }}>¡Todo en orden!</h5>
                                        <p className="text-muted">
                                            No se detectaron áreas críticas que requieran atención inmediata.
                                        </p>
                                    </div>
                                )}
                                
                                {/* RESUMEN EJECUTIVO */}
                                <div className="row mt-4">
                                    <div className="col-md-12">
                                        <div className="card border-0" style={{ 
                                            background: `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}10)`
                                        }}>
                                            <div className="card-body">
                                                <h6 className="mb-3" style={{ color: primaryColor }}>
                                                    <i className="fas fa-bullseye mr-2"></i>
                                                    Resumen Ejecutivo
                                                </h6>
                                                <div className="row">
                                                    <div className="col-md-4 text-center">
                                                        <div className="h2 mb-0" style={{ color: secondaryColor }}>
                                                            {recomendaciones.filter(r => r.prioridad === 'alta').length}
                                                        </div>
                                                        <div className="small text-muted">Prioridad Alta</div>
                                                    </div>
                                                    <div className="col-md-4 text-center">
                                                        <div className="h2 mb-0" style={{ color: '#ffc107' }}>
                                                            {recomendaciones.filter(r => r.prioridad === 'media').length}
                                                        </div>
                                                        <div className="small text-muted">Prioridad Media</div>
                                                    </div>
                                                    <div className="col-md-4 text-center">
                                                        <div className="h2 mb-0" style={{ color: primaryColor }}>
                                                            {recomendaciones.length}
                                                        </div>
                                                        <div className="small text-muted">Recomendaciones Totales</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="mt-3">
                                                    <h6 style={{ color: primaryColor }}>Plan de Acción Sugerido:</h6>
                                                    <ol className="small text-muted">
                                                        <li className="mb-2">Priorice las recomendaciones de <strong>alta prioridad</strong></li>
                                                        <li className="mb-2">Asigne responsables para cada acción</li>
                                                        <li className="mb-2">Establezca fechas límite para implementación</li>
                                                        <li>Monitoree el impacto en el próximo reporte</li>
                                                    </ol>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TablaReporteMovimientos;